import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { ContactLensesData } from "@/types/config.types";
import { LensPrescription } from "@/types/prescription.types";
import { PrescriptionData } from "@/types/prescription.types";

interface ContactLensesStore {
  contactLensesData: ContactLensesData;
  selectedPrescription: PrescriptionData | undefined;
  insDiscount: number | undefined;
  actions: {
    setContactLensesData: (data: ContactLensesData) => void;
    updatePrescriptions: (prescription: {
      OD: LensPrescription;
      OS: LensPrescription;
    }) => void;
    removeOD: () => void;
    removeOS: () => void;
    setSelectedPrescription: (data: PrescriptionData | undefined) => void;
    setInsuranceDiscount: (discount: number | undefined) => void;
    reset: () => void;
  };
}

const initialState: Omit<ContactLensesStore, "actions"> = {
  contactLensesData: {
    OD: { product: null, prescription: null },
    OS: { product: null, prescription: null },
  },
  selectedPrescription: undefined,
  insDiscount: undefined,
};

export const useContactLensesStore = create<ContactLensesStore>()(
  devtools(
    (set) => ({
      contactLensesData: null,
      selectedPrescription: null,
      actions: {
        setContactLensesData: (data: ContactLensesData) => {
          set({ contactLensesData: data });
        },

        updatePrescriptions: (prescription: {
          OD: LensPrescription;
          OS: LensPrescription;
        }) => {
          set((state: ContactLensesStore) => ({
            contactLensesData: {
              ...state.contactLensesData,
              OD: {
                product: state.contactLensesData?.OD?.product,
                prescription: {
                  ...state.contactLensesData?.OD?.prescription,
                  ...prescription.OD,
                },
              },
              OS: {
                product: state.contactLensesData?.OS?.product,
                prescription: {
                  ...state.contactLensesData?.OS?.prescription,
                  ...prescription.OS,
                },
              },
            },
          }));
        },

        removeOD: () => {
          set((state: ContactLensesStore) => ({
            contactLensesData: {
              ...state.contactLensesData,
              OD: { product: null, prescription: null },
            },
          }));
        },

        removeOS: () => {
          set((state: ContactLensesStore) => ({
            contactLensesData: {
              ...state.contactLensesData,
              OS: { product: null, prescription: null },
            },
          }));
        },

        setSelectedPrescription: (data: PrescriptionData) => {
          set({ selectedPrescription: data });
        },

        setInsuranceDiscount: (discount: number) => {
          set({ insDiscount: discount });
        },

        reset: () => {
          set({ ...initialState }, false, "resetState");
        },
      },
    }),
    { name: "ContactLensesStore" },
  ),
);

export default useContactLensesStore;
